import React, { useState } from 'react';
import { Header, Container, Button } from 'semantic-ui-react';
import { Form, CheckboxField, Select, SelectItem } from '@plone/components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { EPICS_PATH_CONTINUE } from 'volto-epics-addon/constants';
import { TASK_UPLOAD_DETAILS_PROMPT } from 'volto-base-addon/constants';
import { completeTask, useTask } from 'volto-epics-addon/helpers';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import { getLanguageOptions } from 'volto-base-addon/helpers/languageOptions';
import UploadStepsContainer from './UploadStepsContainer';
import { useIntl, defineMessages } from 'react-intl';
import config from '@plone/volto/registry';

const messages = defineMessages({
  uploadDetailsDetails: {
    id: 'uploadDetailsDetails',
    defaultMessage: 'Details',
  },
  uploadDetailsAnonymization: {
    id: 'uploadDetailsAnonymization',
    defaultMessage: 'Anonymization',
  },
  uploadDetailsFaceBlur: {
    id: 'uploadDetailsFaceBlur',
    defaultMessage: 'Automatic face blurring',
  },
  uploadDetailsFilters: {
    id: 'uploadDetailsFilters',
    defaultMessage: 'Filters',
  },
  uploadDetailsDynamicNormalizer: {
    id: 'uploadDetailsDynamicNormalizer',
    defaultMessage: 'Dynamic Audio Normalizer',
  },
  uploadDetailsNoiceReducer: {
    id: 'uploadDetailsNoiceReducer',
    defaultMessage: 'Broadband Noise Reducer',
  },
  uploadDetailsSpeechNormalizer: {
    id: 'uploadDetailsSpeechNormalizer',
    defaultMessage: 'Speech Normalizer',
  },
  uploadDetailsLength: {
    id: 'uploadDetailsLength',
    defaultMessage: 'Video Length',
  },
  uploadDetailsSubtitles: {
    id: 'uploadDetailsSubtitles',
    defaultMessage: 'Subtitles',
  },
  uploadDetailsGenerateSubtitles: {
    id: 'uploadDetailsGenerateSubtitles',
    defaultMessage: 'Generate subtitles',
  },
  uploadDetailsDiarization: {
    id: 'uploadDetailsDiarization',
    defaultMessage: 'Speaker diarization',
  },
  uploadDetailsFillerWords: {
    id: 'uploadDetailsFillerWords',
    defaultMessage: 'Include filler words',
  },
  uploadDetailsTranslate: {
    id: 'uploadDetailsTranslate',
    defaultMessage: 'Translate to English',
  },
  uploadDetailsTrim: {
    id: 'uploadDetailsTrim',
    defaultMessage: 'Trim media',
  },
  uploadDetailsLanguageLabel: {
    id: 'uploadDetailsLanguageLabel',
    defaultMessage: 'Speech language',
  },
  uploadDetailsLanguageDescription: {
    id: 'uploadDetailsLanguageDescription',
    defaultMessage:
      'By choosing a language you improve accuracy and precision of final result',
  },
  uploadDetailsLanguagePlaceholder: {
    id: 'uploadDetailsLanguagePlaceholder',
    defaultMessage: 'Select a language',
  },
  mustLoginFirst: {
    id: 'mustLoginFirst',
    defaultMessage: 'You must log in first.',
  },
  uploadDetailsContinue: {
    id: 'uploadDetailsContinue',
    defaultMessage: 'Continue',
  },
});

const Content = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const task = useTask(TASK_UPLOAD_DETAILS_PROMPT);
  const title = task?.variables?.title;
  const destinationTitle = task?.variables?.destinationTitle;
  const token = useSelector((state) => state.userSession.token);
  const [filters, setFilters] = useState({
    dynaudnorm: false,
    denoiser: false,
    speechnorm: false,
  });
  const [subtitles, setSubtitles] = useState({
    transcription: false,
    diarization: false,
    translate: false,
    useInitialPromptTranscription:
      config.settings.defaultUseInitialPrompt ?? false,
  });
  const [doTrim, setDoTrim] = useState(false);
  const [doFaceblur, setDoFaceblur] = useState(false);

  const isAuthenticated = !!token;
  const isVisible = true;

  if (task?.completed === true) {
    history.push(EPICS_PATH_CONTINUE);
  }

  const onSubmit = async (ev) => {
    if (!token) return;

    ev.nativeEvent.preventDefault();
    const subtitleData = {
      ...subtitles,
      useInitialPromptFi:
        subtitles.language === 'fi' && subtitles.useInitialPromptTranscription,
      useInitialPromptEn:
        subtitles.language === 'en' && subtitles.useInitialPromptTranscription,
    };
    delete subtitleData.useInitialPromptTranscription;
    if (subtitleData.language === 'none') delete subtitleData.language;
    try {
      completeTask(TASK_UPLOAD_DETAILS_PROMPT, {
        filters: JSON.stringify(filters),
        faceblur: JSON.stringify(doFaceblur),
        doTrim: doTrim,
        subtitles: JSON.stringify(subtitleData),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return isVisible ? (
    isAuthenticated ? (
      <Container className="details-prompt-container">
        <UploadStepsContainer currentStep={3} totalSteps={4} />
        <Header>{intl.formatMessage(messages.uploadDetailsDetails)}</Header>
        {destinationTitle && title ? (
          <p>
            {destinationTitle} / {title}
          </p>
        ) : null}
        <Form onSubmit={onSubmit}>
          <div className="details-prompt-subtitles">
            <h4>{intl.formatMessage(messages.uploadDetailsSubtitles)}</h4>
            <CheckboxField
              onChange={(value) =>
                setSubtitles((prevState) => ({
                  ...prevState,
                  transcription: value,
                }))
              }
              label={intl.formatMessage(
                messages.uploadDetailsGenerateSubtitles,
              )}
              isSelected={subtitles.transcription}
            ></CheckboxField>
            {subtitles.transcription && (
              <>
                <Select
                  label={intl.formatMessage(
                    messages.uploadDetailsLanguageLabel,
                  )}
                  description={intl.formatMessage(
                    messages.uploadDetailsLanguageDescription,
                  )}
                  placeholder={intl.formatMessage(
                    messages.uploadDetailsLanguagePlaceholder,
                  )}
                  onSelectionChange={(value) =>
                    setSubtitles((prevState) => ({
                      ...prevState,
                      language: value,
                    }))
                  }
                  selectedKey={subtitles['language'] || 'none'}
                >
                  <SelectItem id="none">
                    {intl.formatMessage(
                      messages.uploadDetailsLanguagePlaceholder,
                    )}
                  </SelectItem>
                  {getLanguageOptions(intl.locale).map((lang) => (
                    <SelectItem id={lang.code} key={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </Select>
                <CheckboxField
                  onChange={(value) =>
                    setSubtitles((prevState) => ({
                      ...prevState,
                      diarization: value,
                    }))
                  }
                  label={intl.formatMessage(messages.uploadDetailsDiarization)}
                  isSelected={subtitles.diarization}
                ></CheckboxField>
                {(subtitles.language === 'fi' ||
                  subtitles.language === 'en') && (
                  <CheckboxField
                    onChange={(value) =>
                      setSubtitles((prevState) => ({
                        ...prevState,
                        useInitialPromptTranscription: value,
                      }))
                    }
                    label={intl.formatMessage(
                      messages.uploadDetailsFillerWords,
                    )}
                    isSelected={subtitles.useInitialPromptTranscription}
                  ></CheckboxField>
                )}
                <CheckboxField
                  onChange={(value) =>
                    setSubtitles((prevState) => ({
                      ...prevState,
                      translate: value,
                    }))
                  }
                  label={intl.formatMessage(messages.uploadDetailsTranslate)}
                  isSelected={subtitles.translate}
                ></CheckboxField>
              </>
            )}
          </div>
          <div className="details-prompt-filters">
            <h4>{intl.formatMessage(messages.uploadDetailsFilters)}</h4>
            <CheckboxField
              onChange={(value) =>
                setFilters((prevState) => ({
                  ...prevState,
                  dynaudnorm: value,
                }))
              }
              label={intl.formatMessage(
                messages.uploadDetailsDynamicNormalizer,
              )}
              isSelected={filters.dynaudnorm}
            ></CheckboxField>
            <CheckboxField
              onChange={(value) =>
                setFilters((prevState) => ({
                  ...prevState,
                  denoiser: value,
                }))
              }
              label={intl.formatMessage(messages.uploadDetailsNoiceReducer)}
              isSelected={filters.denoiser}
            ></CheckboxField>
            <CheckboxField
              onChange={(value) =>
                setFilters((prevState) => ({
                  ...prevState,
                  speechnorm: value,
                }))
              }
              label={intl.formatMessage(messages.uploadDetailsSpeechNormalizer)}
              isSelected={filters.speechnorm}
            ></CheckboxField>
          </div>
          <div className="details-prompt-faceblur">
            <h4>{intl.formatMessage(messages.uploadDetailsAnonymization)}</h4>
            <CheckboxField
              onChange={(value) => setDoFaceblur(value)}
              label={intl.formatMessage(messages.uploadDetailsFaceBlur)}
              isSelected={doFaceblur}
            ></CheckboxField>
          </div>
          <div className="details-prompt-range">
            <h4>{intl.formatMessage(messages.uploadDetailsLength)}</h4>
            <CheckboxField
              onChange={(value) => setDoTrim(value)}
              label={intl.formatMessage(messages.uploadDetailsTrim)}
              isSelected={subtitles.doTrim}
            ></CheckboxField>
          </div>
          <Button style={{ marginTop: '1em' }} type="submit" floated="right">
            {intl.formatMessage(messages.uploadDetailsContinue)}
          </Button>
        </Form>
      </Container>
    ) : (
      <Container className="details-prompt-container">
        <Header>{intl.formatMessage(messages.mustLoginFirst)}</Header>
      </Container>
    )
  ) : (
    <Container className="details-prompt-container"></Container>
  );
};

export default injectLazyLibs('reactSelect')(Content);
